import { MarginedBoundingBox } from "../../../../Types/MarginedBoundingBox"
import { Offset } from "../../../../Types/Offset"
import { ModalityGraphConfig } from "../../../../Types/Graph"
import { D3TimeBasedVisualization } from "../../D3TimeBasedVisualization"
import { D3ModalityGraphGroupConfigurationBuilder } from "./D3ModalityGraphGroupConfigurationBuilder"
import { D3ModalityGraphGroupRenderer } from "./D3ModalityGraphGroupRenderer"
import { ModalityGraphGroupReactCallbacks } from "../../../../Types/ReactCallbacks"
import { ModalityGraphGroupConfig } from "../../../../Types/ModalityGraphGroup"
import { TimeSeriesPageManagerConfig } from "../../../../Data/TimeSeriesPageManager"
import { ModalityPage } from "../../../../Data/ModalityPage"
import { ModalityPageManager } from "../../../../Data/ModalityPageManager"
import { getVisibleGraphs } from "../visibleGraphs"
import { LEFT_MARGIN, MULTI_GRAPH_SPACING, NUMERIC_VALUE_MARGIN, RIGHT_MARGIN, TIMELINE_SPACING } from "../../Constants"
import { ModalityDataSource } from "../../../../Types/ModalityDataSource"
import { getQEEGDSARequestAnalysis, isQEEGDSAAnalysis, isQEEGDSASourceModality } from "../../../../Types/AnalysisDetails"
import { FlowsheetConfig, getFullTraceDataConfigs, MedicationConfig, RenderStrategy, TraceConfig, TraceConfigJSON, TraceDataConfig } from "../../../../Types/Trace"
import { Page } from "../../../../Data/Page"
import { uniqueId } from "lodash"
import { jumpAllWindows } from "../../../../Atoms/Visualizations"
import { LinkedWindowEventType } from "../../../../Types/LinkedWindowInfo"
import { EEG_WINDOW_TYPES, eegViewRegistry } from "../../../../Data/EEGViewRegistry"
import { EMRDataStreamId } from "../../../../../../../../Managers/VisualizationManager/ToolBar/Modals/MedicationGraphModalities/Medications"
import { DataSource } from "../../../../Types/DataSource"
import { scaleLinear } from "d3"


export class D3ModalityGraphGroup extends D3TimeBasedVisualization<
	ModalityGraphGroupConfig,
	ModalityGraphGroupReactCallbacks,
	D3ModalityGraphGroupRenderer,
	ModalityPageManager
> {
	public graphsBoundingBox: MarginedBoundingBox
	public graphsMargins: Offset = { top: 25, left: LEFT_MARGIN, bottom: 60 + TIMELINE_SPACING, right: NUMERIC_VALUE_MARGIN }
	public overlayMargins: Offset = { top: MULTI_GRAPH_SPACING, bottom: MULTI_GRAPH_SPACING, left: 0, right: 0 }
	public overlayBoundingBox: MarginedBoundingBox
	public graphs: ModalityGraphConfig[] = []
	public startTime: number | undefined = undefined
	public endTime: number | undefined = undefined

	constructor(root: HTMLDivElement, config: ModalityGraphGroupConfig, pageManager: ModalityPageManager, reactCallbacks: ModalityGraphGroupReactCallbacks) {
		super(root, config, pageManager, reactCallbacks)
		this.graphsBoundingBox = new MarginedBoundingBox(config.dimensions, this.graphsMargins)
		this.overlayBoundingBox = new MarginedBoundingBox({ width: this.graphsBoundingBox.width, height: this.graphsBoundingBox.height }, this.overlayMargins)
		// The numbers on the right read their time from the cursor line.
		this.reactCallbacks.getCursorTime = this.getCursorTime
		this.mount(new D3ModalityGraphGroupRenderer(this, new D3ModalityGraphGroupConfigurationBuilder(this), "d3-modality-graph-group"))
		this.autoScale()
		eegViewRegistry.onChange(this.eegViewListener, () => this.renderer?.graphsOverlay?.cursorChanged())
	}

	private eegViewListener = uniqueId("eeg-views-")

	public unmount() {
		eegViewRegistry.onChange(this.eegViewListener, null)
		super.unmount()
	}

	// A picked time centers every EEG window on it.
	public jumpEEG = (time: number) => {
		jumpAllWindows(new Date(time), LinkedWindowEventType.JUMP_MIDDLE, { autoScale: true, controllerIsLinked: true, windowTypes: EEG_WINDOW_TYPES, pickedTime: true })
	}

	public clearDataAndReload = () => {
		this.timeSeriesPageManager.resetPages()
		this.renderer?.graphsWrapper?.clearSnapshot()
		this.updateConfig(this.config)
		this.timeSeriesPageManager.clearQueueAndLoad()
		this.autoScale()
	}

	protected onPageLoaded = (page: Page<any>) => {
		const modalityPage = page as ModalityPage // to make TS happy
		const pagesInView = this.timeSeriesPageManager.getPagesInView()
		this.renderer?.timeline?.updateNotLoadedRegions()

		if (pagesInView.includes(modalityPage)) {
			this.renderPage(modalityPage)
			this.updateWindowLoadingState()

			if (!this.renderer?.graphsWrapper?.isRescaling()) {
				this.autoScale()
			}
		}
	}

	public autoScale = () => {
		this.renderer?.autoScaleGraphs()
	}

	public getOverlay = () => this.renderer?.graphsOverlay

	public getLastHoveredDate = () => this.getOverlay()?.getLastHoveredDate()

	// The time "Click to pick a time" marks, or null for the latest values. Kept here, not in React
	// state, so a click moves the line and the numbers in the same frame (a React round trip
	// re-rendered the whole window and took about 200 ms). Session only. It stays until the user
	// clicks another time or "Show latest values": panning, paging, the scrubber, a new page size
	// or live mode never move it.
	private cursorTime: number | null = null

	// Every graph and the overlay read the time from here, so they always agree.
	public getCursorTime = (): number | null => (this.config.valueCursor === true ? this.cursorTime : null)

	// A click on the graphs marks a time; null goes back to the latest values.
	public setCursorTime = (time: number | null) => {
		this.cursorTime = time
		this.renderer?.graphsOverlay?.cursorChanged()
		this.renderer?.graphsWrapper?.viewTimesChanged()
	}

	protected renderPage(page: ModalityPage): void {
		this.renderer?.graphsWrapper?.renderPage(page)
	}

	public viewTimesChanged = (startTime: number, endTime: number) => {
		const { viewDuration } = this.getStartTimeEndTimeViewDuration()
		const calculatedViewDuration = endTime - startTime

		if (viewDuration !== calculatedViewDuration) {
			// If the view duration has changed, we need to let React handle the update.
			// Otherwise, we end up reverting the duration back to the old duration.
			// This bug is caused by the updates from another component's timeline when it is playing.
			if (this.anotherLinkedWindowIsPlaying()) {
				return
			}
		}

		this.config.viewScale.domain([startTime, endTime])
		this.renderer?.viewTimesChanged()

		if (!this.renderer?.graphsWrapper?.isRescaling()) {
			this.timeSeriesPageManager?.clearQueueAndLoad()
		}

		this.updateWindowLoadingState()
	}

	public onTimeAxisDragStart = () => {
		this.renderer?.graphsWrapper?.takeSnapshot()
	}

	public onTimeAxisDrag = () => {
		this.renderer?.onTimeAxisDrag()
		this.updateLinkedWindows()
	}

	public onTimelineSliderDrag = () => {
		this.timeSeriesPageManager?.clearQueueAndLoad()
		this.renderer?.onTimelineSliderDrag()
		this.updateLinkedWindows()
	}

	public onTimelineSliderDragEnd = () => {
		this.timeSeriesPageManager?.clearQueueAndLoad()
		this.updateLinkedWindows({ autoScale: true, controllerIsLinked: this.config.isLinked })
		this.autoScale()
	}

	public getVisibleTraces(): TraceDataConfig[] {
		return this.config.graphs?.flatMap(graph => graph.traces.filter(trace => trace.isVisible ?? true).flatMap(getFullTraceDataConfigs))
	}

	public clearCacheAndRedraw = () => {
		this.timeSeriesPageManager.getAllLoadedPages().forEach((page: ModalityPage) => page.clearRenderCache())
		this.timeSeriesPageManager.clearQueueAndLoad()
		this.autoScale()
	}

	// PROTECTED

	protected getModalityDataSources(): ModalityDataSource[] {
		const modalityDataSources = new Set<ModalityDataSource>()

		this.graphs.forEach(graph => {
			const isEMRData = graph.renderStrategy === RenderStrategy.MEDICATION || graph.renderStrategy === RenderStrategy.FLOWSHEET

        	graph.traces.forEach(trace => {
				switch (trace.renderStrategy) {
					case RenderStrategy.DELTA:
						[trace.first, trace.second].forEach(config => {
							modalityDataSources.add(this.getModalityDataSource(config, isEMRData))
						})
						break
					case RenderStrategy.SPECTROGRAM:
						if (trace.onDemandAnalysis === undefined && isQEEGDSASourceModality(trace.rawDataKey)) {
							// Offline (source) DSA: the archive modality itself, no analysis attached.
							modalityDataSources.add({
								modality: trace.dataKey,
								dataObjectId: this.reactCallbacks.dataSourceMap.get(trace.dataSource) ?? Infinity,
							})
						} else if (isQEEGDSAAnalysis(trace.onDemandAnalysis)) {
							// Online DSA
							modalityDataSources.add({
								modality: trace.dataKey,
								dataObjectId: this.reactCallbacks.dataSourceMap.get(trace.dataSource) ?? Infinity,
								onDemandAnalysis: getQEEGDSARequestAnalysis(trace.onDemandAnalysis),
								isEMRData
							})
						}

						break
					default:
						modalityDataSources.add(this.getModalityDataSource(trace, isEMRData))
				}
			})
		})

		return [...modalityDataSources]
	}

	protected updatePageManager(): void {
		super.updatePageManager()
		this.timeSeriesPageManager.updateEMRData(this.config.emrData)
	}

	protected updateDerivedState = () => {
		this.graphsBoundingBox.setDimensions(this.config.dimensions)
		const graphs = this.autoDeriveGraphs(this.config.graphs)
		const visibleGraphs = getVisibleGraphs(graphs, this.config.patientModalities, this.config.emrData, this.config.hideEmptyGraphs)
		const anyNumericDisplays = visibleGraphs.some(graph => graph.traces.some(trace => trace.summaryValueConfig?.isVisible))

		// We change the graphs bounding box depending on if there are numeric displays or not.
		// With "Click to pick a time" on, the column stays even without numbers: the cursor's
		// time is shown at its top.
		// It also takes 15px more above the graphs for the second line under the time.
		const top = this.graphsMargins.top + (this.config.valueCursor === true ? 15 : 0)
		if (this.config.valueCursor !== true) {
			this.cursorTime = null
		}
		if (anyNumericDisplays || this.config.valueCursor === true) {
			this.graphsBoundingBox.setMargins({ ...this.graphsMargins, top, right: NUMERIC_VALUE_MARGIN })
		} else {
			this.graphsBoundingBox.setMargins({ ...this.graphsMargins, top, right: RIGHT_MARGIN })
		}

		// Anything that relies on the graphs bounding box must go below this line
		this.overlayBoundingBox.setDimensions({ height: this.graphsBoundingBox.height, width: this.graphsBoundingBox.width })
		this.config.viewScale.range([0, this.graphsBoundingBox.width])

		// Calculate total height units based on each graph's ratio
		const totalHeightUnits = visibleGraphs.reduce((sum, graph) => sum + (graph.heightRatio || 1), 0)

		// Calculate the available height for graphs (excluding spacing)
		const totalSpacing = MULTI_GRAPH_SPACING * (visibleGraphs.length + 1)
		const availableHeight = this.graphsBoundingBox.height - totalSpacing

		// Calculate base unit height
		const unitHeight = availableHeight / totalHeightUnits

		// Build graphs with proportional heights and accumulated offsets
		let currentOffset = MULTI_GRAPH_SPACING

		this.graphs = visibleGraphs.map((graph) => {
			const heightRatio = graph.heightRatio || 1
			const graphHeight = Math.max(unitHeight * heightRatio, 1)
			
			const graphConfig = {
				...graph,
				xScale: this.config.viewScale,
				height: graphHeight,
				width: this.graphsBoundingBox.width,
				offset: currentOffset,
			};
			
			// Update offset for next graph
			currentOffset += graphHeight + MULTI_GRAPH_SPACING
			
			return graphConfig
		});

		this.renderer?.updateChildren()
	}

	private autoDeriveGraphs = (graphs: ModalityGraphConfig[]): ModalityGraphConfig[] => {
		return graphs.map(graph => {
			switch (graph.renderStrategy) {
				case RenderStrategy.MEDICATION:
					return {...graph, traces: this.getMedicationTraces(graph) }
				case RenderStrategy.FLOWSHEET:
					return {...graph, traces: this.getFlowsheetTraces(graph) }
				case RenderStrategy.HEATMAP:
				case RenderStrategy.LINE:
					return {...graph, traces: this.autoDeriveGraphLineTraces(graph.traces as any) }
			} 

			return graph
		}) as any
	}

	private autoDeriveGraphLineTraces = (traces: Array<TraceConfig>): TraceConfigJSON[] => {
		return traces.map(trace => {
			switch (trace.renderStrategy) {
				case RenderStrategy.DELTA:
					return {
						...trace,
						first: this.getTraceDataConfig(trace.first),
						second: this.getTraceDataConfig(trace.second),
					}
				default:
					return this.getTraceDataConfig(trace)
			}
		}) as any
	}

	private getModalityDataSource(trace: TraceDataConfig, isEMRData: boolean): ModalityDataSource {
		const onDemandAnalysis = trace.onDemandAnalysis

		return {
			modality: onDemandAnalysis ? JSON.stringify(onDemandAnalysis) : trace.dataKey,
			dataObjectId: this.reactCallbacks.dataSourceMap.get(trace.dataSource) ?? Infinity,
			onDemandAnalysis: onDemandAnalysis as any,
			isEMRData
		}
	}

	private getTraceDataConfig(trace: TraceDataConfig): TraceDataConfig {
		const onDemandAnalysis = trace.onDemandAnalysis
		return {
			...trace,
			dataKey: onDemandAnalysis ? JSON.stringify(onDemandAnalysis) : trace.dataKey,
			onDemandAnalysis
		}
	}

	public getPageManagerConfig = (): TimeSeriesPageManagerConfig => {
		return {
			patientId: this.config.patientId,
			patientIdentifier: this.config.patientIdentifier,
			windowId: this.config.id,
			viewScale: this.config.viewScale,
			fileScale: this.config.fileScale,
			modalityDataSources: this.getModalityDataSources(),
			emrData: this.config.emrData,
			timeZone: this.config.timeZone,
			patientIsAdmitted: this.config.patientIsAdmitted,
			environmentVariables: this.config.environmentVariables,
			liveModeEnabled: this.config.liveModeEnabled
		}
	}

	private getMedicationTraces = (graph: ModalityGraphConfig): MedicationConfig[] => {
		if (!graph.filters) {
			return []
		}

		return graph.filters.flatMap(filter => {
			const foundHarmonizedData = this.config.emrData.find(harmonizedMedication => harmonizedMedication.id.toString() === filter.id)

			if (!foundHarmonizedData) {
				const emrDataId: EMRDataStreamId = { id: Number(filter.id), code: filter.name, units: "" }
				const dataKey = JSON.stringify(emrDataId)
				
				return [{
					id: dataKey,
					dataKey: dataKey,
					rawDataKey: dataKey,
					dataSource: DataSource.CURRENT_PATIENT,
					name: filter.name,
					units: "",
					isCompositePart: false,
					color: "$000000",
					renderStrategy: RenderStrategy.INTERMITTENT_MEDICATION,
					graphId: graph.id,
					xScale: this.config.viewScale,
					yScale: scaleLinear(),
					class: "Unknown",
					isEMRData: true,
					isVisible: false,
					summaryValueConfig: {
						color: "#000000",
						name: filter.name,
						precision: 2,
						isVisible: false,
						units: ""
					}
				}]
			}

			return foundHarmonizedData.dataStreams.map(stream => {
				const emrDataId: EMRDataStreamId = { id: foundHarmonizedData.id, code: stream.code, units: stream.units }
				const dataKey = JSON.stringify(emrDataId)
				const isVisible = this.isEMRDataVisible(graph.id, dataKey)

				const traceConfig: MedicationConfig = {
					id: dataKey,
					dataKey: dataKey,
					rawDataKey: foundHarmonizedData.name,
					dataSource: DataSource.CURRENT_PATIENT,
					name: foundHarmonizedData.name,
					units: stream.units,
					isCompositePart: false,
					color: foundHarmonizedData.color,
					renderStrategy: stream.isContinuous ? RenderStrategy.INFUSION_MEDICATION : RenderStrategy.INTERMITTENT_MEDICATION,
					graphId: graph.id,
					xScale: this.config.viewScale,
					yScale: scaleLinear(),
					class: foundHarmonizedData.class,
					isEMRData: true,
					isVisible,
					summaryValueConfig: {
						color: foundHarmonizedData.color,
						name: foundHarmonizedData.name,
						precision: 2,
						isVisible,
						units: stream.units
					}
				}

				return traceConfig
			})
		})
	}

	private getFlowsheetTraces = (graph: ModalityGraphConfig): FlowsheetConfig[] => {
		if (!graph.filters) {
			return []
		}

		return graph.filters?.flatMap(filter => {
			const foundHarmonizedData = this.config.emrData.find(harmonizedMedication => harmonizedMedication.id.toString() === filter.id)

			if (!foundHarmonizedData) {
				const emrDataId: EMRDataStreamId = { id: Number(filter.id), code: filter.name, units: "" }
				const dataKey = JSON.stringify(emrDataId)

				const flowsheetConfig: FlowsheetConfig = {
					id: dataKey,
					dataKey: dataKey,
					rawDataKey: dataKey,
					dataSource: DataSource.CURRENT_PATIENT,
					name: filter.name,
					units: "",
					isCompositePart: false,
					color: "$000000",
					renderStrategy: RenderStrategy.POINT,
					graphId: graph.id,
					xScale: this.config.viewScale,
					yScale: scaleLinear(),
					isEMRData: true,
					isVisible: false,
					summaryValueConfig: {
						color: "#000000",
						name: filter.name,
						precision: 0,
						isVisible: false,
						units: ""
					}
				}

				return [flowsheetConfig]
			}

			return foundHarmonizedData.dataStreams.map(stream => {
				const emrDataId: EMRDataStreamId = { id: foundHarmonizedData.id, code: stream.code, units: stream.units }
				const dataKey = JSON.stringify(emrDataId)
				const isVisible = this.isEMRDataVisible(graph.id, dataKey)
				
				const traceConfig: FlowsheetConfig = {
					id: dataKey,
					dataKey: dataKey,
					rawDataKey: foundHarmonizedData.name,
					dataSource: DataSource.CURRENT_PATIENT,
					name: foundHarmonizedData.name,
					units: stream.units,
					isCompositePart: false,
					color: foundHarmonizedData.color,
					renderStrategy: stream.isContinuous ? RenderStrategy.LINE_STEP : RenderStrategy.POINT,
					graphId: graph.id,
					xScale: this.config.viewScale,
					yScale: scaleLinear(),
					isEMRData: true,
					isVisible,
					summaryValueConfig: {
						color: foundHarmonizedData.color,
						name: foundHarmonizedData.name,
						precision: 0,
						isVisible,
						units: stream.units
					}
				}

				return traceConfig
			})
		})
	}

	private isEMRDataVisible = (graphId: string, dataKey: string): boolean => {
		const entry = this.config.EMRDataConfigs?.find(entry => entry.graphId === graphId && entry.traceId === dataKey)

		if (entry) {
			return entry.isVisible
		}

		return true
	}
}
